import axios from 'axios'
import { syncServerTime } from '@/utils/serverTime'

// 1. 集中管理環境變數配置 (擴充性更好)
const ENV_CONFIG = {
  1: { // Identity 1
    base: process.env.VUE_APP_API_DOMAIN,
    get: process.env.VUE_APP_API_DOMAIN_GET,
    download: process.env.VUE_APP_API_DOMAIN_DOWNLOAD
  },
  2: { // Identity 2 (Vendor)
    base: process.env.VUE_APP_API_DOMAIN_VENDOR,
    get: process.env.VUE_APP_API_DOMAIN_GET_VENDOR,
    download: process.env.VUE_APP_API_DOMAIN_DOWNLOAD_VENDOR
  }
};

// 內部變數，允許透過 setIdentity 動態修改
let currentIdentity = null;

// 2. 輔助函式：取得當前配置
// 優先使用 setIdentity 設定的值，其次讀取 LocalStorage，預設為 1
const getCurrentConfig = () => {
  if (currentIdentity) return ENV_CONFIG[currentIdentity] || ENV_CONFIG[1];

  try {
    const loginInfo = JSON.parse(localStorage.getItem("LoginInfo"));
    const id = loginInfo?.Identity ?? 1;
    return ENV_CONFIG[id] || ENV_CONFIG[1];
  } catch (e) {
    return ENV_CONFIG[1];
  }
};

// 3. 輔助函式：FormData 轉換 (保持原邏輯但稍微整理)
const objectToFormData = (obj) => {
  const formData = new FormData();
  if (!obj) return formData;

  Object.keys(obj).forEach(key => {
    const value = obj[key];
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item instanceof File) {
          formData.append(key, item);
        } else {
          formData.append(`${key}[]`, item);
        }
      });
    } else if (typeof value === 'object' && value !== null && !(value instanceof File)) {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, (value === null || value === undefined) ? '' : value);
    }
  });
  return formData;
};

// 4. 通用攔截器設定 (解決重複代碼)
const setupInterceptors = (instance, urlType = 'base') => {
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('AuthorizationToken');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      // *** 關鍵優化：動態設定 baseURL ***
      // 這樣即使切換身份也不用刷新頁面
      const env = getCurrentConfig();
      config.baseURL = env[urlType];

      return config;
    },
    (error) => Promise.reject(error)
  );

  // 建議加入 Response 攔截器處理通用錯誤 (可選)
  instance.interceptors.response.use(
    response => {
      syncServerTime(response);
      return response;
    },
    error => {
      syncServerTime(error?.response);
      // 這裡可以處理 401 登出邏輯
      return Promise.reject(error);
    }
  );
};

// 5. 建立實例
const createInstance = (headers = {}, urlType = 'base') => {
  const instance = axios.create({
    timeout: 30000,
    headers: headers
  });
  setupInterceptors(instance, urlType);
  return instance;
};

const getInstance = createInstance({ 'accept': 'text/plain' }, 'get');
const postInstance = createInstance({ 'Content-Type': 'application/json' }, 'base');
const formInstance = createInstance({ 'Content-Type': 'multipart/form-data' }, 'base');
const downloadInstance = createInstance({ 'accept': '*/*' }, 'download');

// 6. 匯出物件與方法封裝
const r = {};

// 允許外部強制設定身份
r.setIdentity = (id) => {
  currentIdentity = id;
};

// 通用請求包裝 (使用 try...finally 確保 loading 正確關閉)
const wrapRequest = async (requestFn, loading) => {
  if (loading) loading.value = true;
  try {
    return await requestFn();
  } catch (error) {
    console.error('API Error:', error); // 建議保留錯誤日誌
    throw error; // 繼續拋出錯誤讓調用端知道失敗
  } finally {
    if (loading) loading.value = false;
  }
};

r.get = (url, para = null, loading = null) => {

  return wrapRequest(() => getInstance.get(url, { params: para }), loading);
};

r.post = (url, para = null, loading = null) => {
  return wrapRequest(() => postInstance.post(url, para), loading);
};

r.formPost = (url, para = null, loading = null) => {
  // 注意：objectToFormData 移入這裡確保轉換邏輯一致
  return wrapRequest(() => formInstance.post(url, objectToFormData(para)), loading);
};

r.getbase64 = async (fileId) => {
  return await downloadInstance.get(`/File/previewBase64/${fileId}`);
};

r.download = async (url, fileName = null, params = null) => {
  try {
    if (!url) return;

    const config = {
      responseType: 'blob',
      ...params
    };

    // 使用 downloadInstance，且攔截器會自動填入正確的 baseURL
    let result = await downloadInstance.get(`/File/downloadStream/${url}`, config);

    if (!fileName) {
      const contentDisposition = result.headers['content-disposition'];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }
    }

    // 解碼檔名 (防止中文亂碼)
    if (fileName) {
      try { fileName = decodeURIComponent(fileName); } catch (e) { console.log(e) }
    } else {
      fileName = 'download_' + new Date().getTime();
    }

    const blob = new Blob([result.data]);
    const linkUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = linkUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    // 清理
    document.body.removeChild(link);
    window.URL.revokeObjectURL(linkUrl);

    return { success: true, fileName, size: result.data.size };

  } catch (error) {
    console.error('下載失敗:', error);
    return { success: false, error: error.message };
  }
};

export default r;